import { useState, useEffect, useCallback } from "react";

interface CookieOptions {
	expires?: number; // days
	path?: string;
	domain?: string;
	secure?: boolean;
	sameSite?: "strict" | "lax" | "none";
}

interface UserPreferences {
	darkMode: boolean;
	sidebarCollapsed: boolean;
	fontSize: number;
	consoleHidden: boolean;
	sidebarWidth: number;
	editorHeight: number;
}

const DEFAULT_PREFERENCES: UserPreferences = {
	darkMode: false,
	sidebarCollapsed: true,
	fontSize: 18,
	consoleHidden: false,
	sidebarWidth: 400,
	editorHeight: 67,
};

const COOKIE_NAME = "pyblaze_preferences";
const COOKIE_OPTIONS: CookieOptions = {
	expires: 365, // 1 year
	path: "/",
	sameSite: "lax",
};

// Cookie utility functions
export const setCookie = (
	name: string,
	value: string,
	options: CookieOptions = {},
) => {
	let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

	if (options.expires) {
		const date = new Date();
		date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
		cookieString += `; expires=${date.toUTCString()}`;
	}

	if (options.path) {
		cookieString += `; path=${options.path}`;
	}

	if (options.domain) {
		cookieString += `; domain=${options.domain}`;
	}

	if (options.secure) {
		cookieString += `; secure`;
	}

	if (options.sameSite) {
		cookieString += `; samesite=${options.sameSite}`;
	}

	document.cookie = cookieString;
};

export const getCookie = (name: string): string | null => {
	const nameEQ = encodeURIComponent(name) + "=";
	const ca = document.cookie.split(";");

	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		while (c.charAt(0) === " ") c = c.substring(1, c.length);
		if (c.indexOf(nameEQ) === 0) {
			return decodeURIComponent(c.substring(nameEQ.length, c.length));
		}
	}

	return null;
};

export const deleteCookie = (name: string, path: string = "/") => {
	document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
};

export const useCookiePreferences = () => {
	const [preferences, setPreferences] = useState<UserPreferences>(() => {
		if (typeof window === "undefined") {
			return DEFAULT_PREFERENCES;
		}

		try {
			const cookieValue = getCookie(COOKIE_NAME);
			if (cookieValue) {
				const parsedPreferences = JSON.parse(cookieValue);
				return {
					...DEFAULT_PREFERENCES,
					...parsedPreferences,
				};
			}
		} catch (error) {
			console.warn("Failed to parse preferences cookie:", error);
		}

		// Initialize with system preference for dark mode if no cookie exists
		const systemDarkMode = window.matchMedia(
			"(prefers-color-scheme: dark)",
		).matches;

		return {
			...DEFAULT_PREFERENCES,
			darkMode: systemDarkMode,
		};
	});

	// Save preferences to cookie whenever they change
	useEffect(() => {
		if (typeof window !== "undefined") {
			try {
				const cookieValue = JSON.stringify(preferences);
				setCookie(COOKIE_NAME, cookieValue, COOKIE_OPTIONS);
			} catch (error) {
				console.error("Failed to save preferences to cookie:", error);
			}
		}
	}, [preferences]);

	// Apply dark mode class to document
	useEffect(() => {
		const root = document.documentElement;
		if (preferences.darkMode) {
			root.classList.add("dark");
		} else {
			root.classList.remove("dark");
		}
	}, [preferences.darkMode]);

	// Listen for system preference changes (only if user hasn't manually set a preference)
	useEffect(() => {
		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

		const handleChange = (e: MediaQueryListEvent) => {
			// Only update if user hasn't manually set a preference
			const cookieValue = getCookie(COOKIE_NAME);
			if (!cookieValue) {
				setPreferences((prev) => ({ ...prev, darkMode: e.matches }));
			}
		};

		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, []);

	const updatePreference = useCallback(
		<K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
			setPreferences((prev) => ({ ...prev, [key]: value }));
		},
		[],
	);

	const toggleDarkMode = useCallback(() => {
		setPreferences((prev) => ({ ...prev, darkMode: !prev.darkMode }));
	}, []);

	const toggleSidebarCollapsed = useCallback(() => {
		setPreferences((prev) => ({
			...prev,
			sidebarCollapsed: !prev.sidebarCollapsed,
		}));
	}, []);

	const toggleConsoleHidden = useCallback(() => {
		setPreferences((prev) => ({ ...prev, consoleHidden: !prev.consoleHidden }));
	}, []);

	const updateFontSize = useCallback((fontSize: number) => {
		const clampedSize = Math.min(Math.max(fontSize, 8), 24);
		setPreferences((prev) => ({ ...prev, fontSize: clampedSize }));
	}, []);

	const increaseFontSize = useCallback(() => {
		setPreferences((prev) => ({
			...prev,
			fontSize: Math.min(prev.fontSize + 1, 24),
		}));
	}, []);

	const decreaseFontSize = useCallback(() => {
		setPreferences((prev) => ({
			...prev,
			fontSize: Math.max(prev.fontSize - 1, 8),
		}));
	}, []);

	const resetFontSize = useCallback(() => {
		setPreferences((prev) => ({ ...prev, fontSize: 12 }));
	}, []);

	const updateSidebarWidth = useCallback((width: number) => {
		const clampedWidth = Math.max(180, Math.min(600, width));
		setPreferences((prev) => ({ ...prev, sidebarWidth: clampedWidth }));
	}, []);

	const updateEditorHeight = useCallback((height: number) => {
		const clampedHeight = Math.max(30, Math.min(80, height));
		setPreferences((prev) => ({ ...prev, editorHeight: clampedHeight }));
	}, []);

	const resetPreferences = useCallback(() => {
		setPreferences(DEFAULT_PREFERENCES);
		deleteCookie(COOKIE_NAME);
	}, []);

	return {
		preferences,
		updatePreference,
		toggleDarkMode,
		toggleSidebarCollapsed,
		toggleConsoleHidden,
		updateFontSize,
		increaseFontSize,
		decreaseFontSize,
		resetFontSize,
		updateSidebarWidth,
		updateEditorHeight,
		resetPreferences,
		// Individual preference getters for convenience
		isDarkMode: preferences.darkMode,
		isSidebarCollapsed: preferences.sidebarCollapsed,
		isConsoleHidden: preferences.consoleHidden,
		fontSize: preferences.fontSize,
		sidebarWidth: preferences.sidebarWidth,
		editorHeight: preferences.editorHeight,
	};
};
